import type { Property } from "@survey-types/survey.property.types";

export type TourNumberingMode = "RENUMBER" | "KEEP";

export const DEFAULT_TOUR_NUMBERING_MODE: TourNumberingMode = "RENUMBER";

const TOUR_NUMBERING_STORAGE_PREFIX = "tourNumberingMode";

export const getPersistedTourNumberingMode = (
  surveyId?: string,
): TourNumberingMode | null => {
  if (!surveyId) return null;

  const raw = localStorage.getItem(`${TOUR_NUMBERING_STORAGE_PREFIX}:${surveyId}`);
  if (raw === "RENUMBER" || raw === "KEEP") {
    return raw;
  }

  return null;
};

export const setPersistedTourNumberingMode = (
  surveyId: string,
  mode: TourNumberingMode,
): void => {
  localStorage.setItem(`${TOUR_NUMBERING_STORAGE_PREFIX}:${surveyId}`, mode);
};

export const resolveTourNumberingMode = (
  surveyId?: string,
  backendMode?: TourNumberingMode,
): TourNumberingMode => {
  if (backendMode === "RENUMBER" || backendMode === "KEEP") {
    return backendMode;
  }

  return getPersistedTourNumberingMode(surveyId) ?? DEFAULT_TOUR_NUMBERING_MODE;
};

export const getTourPropertyDisplayNumbers = ({
  properties,
  mode,
}: {
  properties: Property[];
  mode?: TourNumberingMode;
}): Record<string, number | undefined> => {
  const effectiveMode = mode ?? DEFAULT_TOUR_NUMBERING_MODE;

  // Trust the caller-supplied order (Redux array order is authoritative).
  // Do not re-sort by .order — it drifts when individual property PATCH
  // responses replace one property's server-canonical .order while other
  // properties retain optimistic values from a prior reorder.
  const eligibleProperties = properties.filter(
    (property) => !property.isHidden && !property.isCurrentLocation,
  );

  const numbers: Record<string, number | undefined> = {};

  if (effectiveMode === "KEEP") {
    eligibleProperties.forEach((property, index) => {
      numbers[property.propertyId] = index + 1;
    });
    return numbers;
  }

  let renumberedIndex = 1;
  eligibleProperties.forEach((property) => {
    if (property.isIncludeTourSchedule !== false) {
      numbers[property.propertyId] = renumberedIndex;
      renumberedIndex += 1;
    }
  });

  return numbers;
};